import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import { ArrowLeft } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useChatMessages } from '@chainlit/react-client';

import { AgentSettingsTab } from '@/components/DMAdminComponents/AgentSettingsTab';
import { DisclosureTab } from '@/components/DMAdminComponents/DisclosureTab';
import { FeatureAccessTab } from '@/components/DMAdminComponents/FeatureAccessTab';
import { ManagedAgentAccessTab } from '@/components/DMAdminComponents/ManagedAgentAccessTab';
import { SettingsTab } from '@/components/DMAdminComponents/SettingsTab';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { VerticalTabsList } from '@/components/ui/vertical-tabs-list';


const BASE_TABS = [
    { value: 'settings', label: 'General' },
    { value: 'disclosure', label: 'Disclosure' },
    { value: 'feature-access', label: 'Feature Access' },
    { value: 'agent-access', label: 'Agent Access' }
];

const CLAUDE_SETTINGS_TAB = {
    value: 'claude-settings',
    label: 'Agent Settings'
};

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const { threadId } = useChatMessages();
    const tabs = [...BASE_TABS, CLAUDE_SETTINGS_TAB];

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    const handleBack = () => {
        if (threadId) {
            navigate(`/thread/${threadId}`);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="absolute inset-0 flex flex-col bg-linear-to-br from-background via-background to-muted/20 dark:to-muted/10 overflow-hidden">
            <div className="mx-auto px-4 sm:px-12 pt-8 sm:pt-12 w-full max-w-7xl shrink-0">
                <div className="flex flex-col gap-6">
                    <Button
                        onClick={handleBack}
                        variant="ghost"
                        size="large"
                        className="group justify-start -ml-5 w-fit text-foreground hover:text-foreground"
                    >
                        <span className="relative w-5 h-5 overflow-hidden">
                            <ArrowLeft className="absolute inset-0 transition-transform group-hover:-translate-x-full duration-300 ease-out" />

                            <ArrowLeft className="absolute inset-0 transition-transform translate-x-full group-hover:translate-x-0 duration-300 ease-out" />
                        </span>
                        Back
                    </Button>
                    <div className="space-y-0.5">
                        <h1 className="font-medium text-2xl leading-8">
                            Admin settings panel
                        </h1>
                        <p className="text-muted-foreground text-base">
                            Manage your organization settings, configurations
                            and agents
                        </p>
                    </div>
                </div>

                <div className="mt-6 md:mt-10 border-border border-t" />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain custom-scrollbar scrollbar-gutter-both">
                <div className="mx-auto px-4 sm:px-12 pt-6 md:pt-10 pb-6 md:pb-8 w-full max-w-7xl">
                    <Tabs
                        defaultValue="settings"
                        orientation="vertical"
                        className="flex md:flex-row flex-col items-stretch md:items-start gap-6 md:gap-10 w-full"
                    >
                        <VerticalTabsList
                            tabs={tabs}
                            className="md:top-10 static md:sticky shrink-0"
                        />
                        <div className="hidden md:block self-stretch border-border border-l" />
                        <div className="flex-1 min-w-0">
                            <TabsContent value="settings" className="mt-0">
                                <SettingsTab mgmtApi={mgmtApi} />
                            </TabsContent>
                            <TabsContent value="disclosure" className="mt-0">
                                <DisclosureTab />
                            </TabsContent>
                            <TabsContent
                                value="feature-access"
                                className="mt-0"
                            >
                                <FeatureAccessTab mgmtApi={mgmtApi} />
                            </TabsContent>
                            <TabsContent value="agent-access" className="mt-0">
                                <ManagedAgentAccessTab mgmtApi={mgmtApi} />
                            </TabsContent>
                            <TabsContent
                                value="claude-settings"
                                className="mt-0"
                            >
                                <AgentSettingsTab mgmtApi={mgmtApi} />
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>
        </div>
    );
};

export default AdminPage;
